import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { isDefined, SERVER_DATE_FORMAT } from '../../utils';

export type MonthTranslationMap = Record<string, string>;

export type UseDailyResolutionChartXAxisParams<T> = {
  dataPoints: T[];
  months: MonthTranslationMap;
  /** Maximum amount of ticks regardless of the selected range */
  tickCap?: number | undefined;
  /** Converts whatever data is used for the chart entries to a 'yyyy-MM-dd' format string */
  getDate: (dataPoint: T) => Date;
};

export type DailyResolutionChartXAxisConfig = {
  formatLabel: (timestamp: number) => string[];
  preferredTickCount: number;
  isMultiline: boolean;
  tickValues: number[];
};

export type DailyResolutionChartXAxisBaseInfo = {
  showDay: boolean;
  showYearPrefix: boolean;
  yearOnOwnLine: boolean;
  maxTickNumber: number;
};

export const useDailyResolutionChartXAxisCommon = <T>({
  dataPoints,
  months,
  getDate,
  tickCap = 6,
}: UseDailyResolutionChartXAxisParams<T>): DailyResolutionChartXAxisConfig => {
  const entryToDate = useMemo<Map<T, Date>>(() => {
    const entryToDateMap = new Map<T, Date>();
    for (const dataPoint of dataPoints) {
      entryToDateMap.set(dataPoint, getDate(dataPoint));
    }
    return entryToDateMap;
  }, [dataPoints, getDate]);

  const { dayCount, isMultipleYear, monthCount } = useMemo(() => {
    const uniqueDays = new Set<string>();
    const uniqueMonths = new Set<number>();
    const uniqueYears = new Set<number>();

    dataPoints.forEach(dataPoint => {
      const date = entryToDate.get(dataPoint);
      if (!isDefined(date)) {
        throw new Error(`Date is not defined for data point: ${JSON.stringify(dataPoint)}`);
      }
      uniqueDays.add(format(date, SERVER_DATE_FORMAT));
      uniqueMonths.add(date.getMonth());
      uniqueYears.add(date.getFullYear());
    });

    return {
      dayCount: uniqueDays.size,
      monthCount: uniqueMonths.size,
      isMultipleYear: uniqueYears.size > 1,
    };
  }, [dataPoints, entryToDate]);

  const { showDay, showYearPrefix, yearOnOwnLine, maxTickNumber } = useMemo<DailyResolutionChartXAxisBaseInfo>(() => {
    if (dayCount < 60) {
      return {
        showDay: true,
        showYearPrefix: isMultipleYear,
        yearOnOwnLine: isMultipleYear,
        maxTickNumber: Math.min(dayCount, 4, tickCap),
      };
    } else if (dayCount < 180 && monthCount < 4) {
      return {
        showDay: true,
        showYearPrefix: isMultipleYear,
        yearOnOwnLine: isMultipleYear,
        maxTickNumber: Math.min(dayCount, 4, tickCap),
      };
    } else if (dayCount < 180) {
      return {
        showDay: false,
        showYearPrefix: isMultipleYear,
        yearOnOwnLine: isMultipleYear,
        maxTickNumber: Math.min(4, tickCap),
      };
    } else {
      return {
        showDay: false,
        showYearPrefix: isMultipleYear,
        yearOnOwnLine: isMultipleYear,
        maxTickNumber: Math.min(6, tickCap),
      };
    }
  }, [dayCount, isMultipleYear, monthCount, tickCap]);

  const formatLabel = useCallback(
    (timestamp: number) => {
      const date = new Date(timestamp);
      const englishMonth = date.toLocaleDateString('en-US', { month: 'long' }).toLowerCase();
      const translatedMonth = months[englishMonth] ?? englishMonth;
      const monthName = translatedMonth.toUpperCase();
      const day = date.getDate();
      const baseLabel = showDay ? `${monthName} ${day}` : monthName;

      if (showYearPrefix) {
        const yearPrefix = `'${date.getFullYear().toString().slice(-2)}`;
        return yearOnOwnLine ? [yearPrefix, baseLabel] : [`${yearPrefix} ${baseLabel}`];
      }

      return [baseLabel];
    },
    [months, showDay, showYearPrefix, yearOnOwnLine],
  );

  const tickValues = useMemo<number[]>(
    () =>
      dataPoints
        .filter((_, i) => i % Math.ceil(dataPoints.length / 365) === 0)
        .map(d => entryToDate.get(d)?.getTime() ?? 0),
    [dataPoints, entryToDate],
  );

  return {
    formatLabel,
    preferredTickCount: maxTickNumber,
    isMultiline: isMultipleYear,
    tickValues,
  };
};
